import _ from 'lodash';
import { ChevronDownIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
    FeaturedStockRelatedNewsResponse,
    FeaturedStockResponse,
} from '../../../server/response/responseSchema';
import deviceStore from '../../stores/deviceStore';
import modalStore from '../../stores/modalStore';
import Box from '../../uikit/Box';
import Frame from '../../uikit/Frame';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import Jsonld from '../Jsonld';
import MetaHeader from '../MetaHeader';
import Carousel, { CarouselProvider } from '../common/Carousel';
import { CommonTimeFormat } from '../common/CommonDateFormat';
import { CommonLink } from '../common/CommonLink';
import { MarkdownViewer } from '../common/editor/MarkdownViewer';
import { twMerge } from 'tailwind-merge';

class ViewStore {
    featuredStockList: FeaturedStockResponse[] = [];
    featuredStockRelatedNews: FeaturedStockRelatedNewsResponse = {};
    isLoading = true;
    isHovered = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;

        const [featuredStockList, featuredStockRelatedNews] = await Promise.all([
            apiClient.featuredStock.public.getFeaturedStockList.$query(),
            apiClient.featuredStock.public.getFeaturedStockRelatedNews.$query(),
        ]);

        runInAction(() => {
            this.featuredStockList = _.castArray(featuredStockList);
            this.featuredStockRelatedNews = featuredStockRelatedNews;

            this.isLoading = false;
        });
    }

    setHovered(hovered: boolean) {
        this.isHovered = hovered;
    }
}

const FeaturedStockPage = observer(() => {
    const [searchParams, setSearchParams] = useSearchParams();
    const viewStore = useMobxInstance(ViewStore);

    const description =
        '지금 해외 주요 뉴스에서 화제가 되고 있는 종목을 확인해보세요. 이슈와 함께 주목받는 종목 정보를 빠르고 쉽게 전달합니다.';

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    const featuredStockList = viewStore.featuredStockList;
    const featuredStockRelatedNews = viewStore.featuredStockRelatedNews;

    const ticker = searchParams.get('ticker');
    const currentStockIndex = ticker
        ? featuredStockList.findIndex((item) => item.ticker === ticker)
        : 0;

    const validStockIndex = currentStockIndex >= 0 ? currentStockIndex : 0;
    const lastUpdatedAt = _.first(featuredStockList)?.featuredAt;

    return (
        <Frame>
            <MetaHeader title="화제의 종목" description={description} />
            <Jsonld type="Blog" title="화제의 종목" description={description} />
            <Frame.Title title="화제의 종목" />
            <Frame.Description>최근 뉴스에서 언급되고 있는 종목입니다.</Frame.Description>
            <Box
                className="relative flex w-full grow flex-col px-0"
                isLoading={viewStore.isLoading}
                onMouseEnter={(e) => {
                    viewStore.setHovered(true);
                }}
                onMouseLeave={(e) => {
                    viewStore.setHovered(false);
                }}
            >
                <CarouselProvider
                    onSlideChange={(slideIndex) => {
                        const selectedStock = featuredStockList[slideIndex];
                        if (selectedStock && selectedStock.ticker !== ticker) {
                            setSearchParams(
                                (prev) => {
                                    prev.set('ticker', selectedStock.ticker);
                                    return prev;
                                },
                                {
                                    preventScrollReset: true,
                                    replace: true,
                                },
                            );
                        }
                    }}
                    emblaOptions={{
                        loop: true,
                    }}
                    autoHeight={true}
                    currentSlideIndex={validStockIndex}
                >
                    <Carousel>
                        <Carousel.Container>
                            {featuredStockList.map((featuredStock) => (
                                <Carousel.Slide key={featuredStock.ticker}>
                                    <FeaturedStockListItem
                                        featuredStock={featuredStock}
                                        featuredStockRelatedNews={featuredStockRelatedNews}
                                    />
                                </Carousel.Slide>
                            ))}
                        </Carousel.Container>
                    </Carousel>

                    {/* 이전 버튼 (데스크탑 전용) */}
                    {deviceStore.hasHoverCapability && (
                        <Carousel.PrevCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -left-5.5 -translate-y-1/2"
                            size="lg"
                        />
                    )}

                    {/* 다음 버튼 (데스크탑 전용) */}
                    {deviceStore.hasHoverCapability && (
                        <Carousel.NextCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -right-5.5 -translate-y-1/2"
                            size="lg"
                        />
                    )}

                    <Carousel.Dots className="mt-6" />
                </CarouselProvider>
            </Box>
            {lastUpdatedAt && (
                <div className="text-base-content/60 w-full px-5 text-right text-xs">
                    <CommonTimeFormat time={lastUpdatedAt} fromNow={true} /> 업데이트
                </div>
            )}
        </Frame>
    );
});

const FeaturedStockListItem = observer(
    ({
        featuredStock,
        featuredStockRelatedNews,
    }: {
        featuredStock: FeaturedStockResponse;
        featuredStockRelatedNews: FeaturedStockRelatedNewsResponse;
    }) => {
        const relatedNews = featuredStockRelatedNews[featuredStock.hash];

        return (
            <article className={twMerge('flex flex-col justify-between px-5')}>
                <div className="flex shrink-0 grow-0 items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <div className="text-lg font-bold">{featuredStock.stock.nameKo}</div>
                        {featuredStock.newsImpact === 'positive' && (
                            <div className="badge badge-xs badge-outline badge-success shrink-0">
                                긍정
                            </div>
                        )}
                        {featuredStock.newsImpact === 'neutral' && (
                            <div className="badge badge-xs badge-outline shrink-0">중립</div>
                        )}
                        {featuredStock.newsImpact === 'negative' && (
                            <div className="badge badge-xs badge-outline badge-error shrink-0">
                                부정
                            </div>
                        )}
                    </div>
                    <button
                        className="badge badge-soft badge-md rounded-sm font-semibold"
                        onClick={(e) => {
                            e.preventDefault();
                            modalStore.stock({
                                ticker: featuredStock.ticker,
                            });
                        }}
                    >
                        {featuredStock.ticker}
                        <ChevronDownIcon className="size-3" />
                    </button>
                </div>
                <div className="mt-4 shrink-0 grow-0">
                    <MarkdownViewer
                        markdown={[
                            featuredStock.reasonSummary,
                            ...featuredStock.reason.map((reason) => `- ${reason}`),
                        ].join('\n')}
                    />
                </div>
                {_.isEmpty(relatedNews) ? null : (
                    <div className="bg-base-200 mt-5 shrink grow rounded p-4 text-sm">
                        <div className="mb-2 text-sm font-semibold">관련 뉴스</div>
                        <ul className="overflow-y-scroll">
                            {relatedNews.map((news) => (
                                <li key={news.hash} className="py-1">
                                    <CommonLink
                                        to={`/news/${news.slug}`}
                                        className="link link-hover"
                                        preventScrollReset={false}
                                    >
                                        - {news.title}{' '}
                                        <span className="text-base-content/60 text-xs">
                                            <CommonTimeFormat
                                                time={news.publishedAt}
                                                fromNow={true}
                                            />
                                        </span>
                                    </CommonLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </article>
        );
    },
);

export default FeaturedStockPage;
